import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { apiClient } from '../api/client'
import type { Job } from '../types'
import JobStatusPanel from '../components/JobStatusPanel'
import PageHeader from '../components/ui/PageHeader'
import { ArrowLeft, AlertCircle, CheckCircle2, Clock, FileText } from 'lucide-react'

interface JobDebug {
  job_id: string
  status: string
  llm_provider: string
  is_mock: boolean
  extraction_summary: Record<string, unknown>
  validation: Record<string, unknown>
  parser_warnings: string[]
  text_preview: string
  raw_text_url: string | null
}

export default function JobDetailPage() {
  const { jobId } = useParams<{ jobId: string }>()
  const navigate = useNavigate()
  const [job, setJob] = useState<Job | null>(null)
  const jobRef = useRef<Job | null>(null)
  if (jobRef.current !== job) {
    jobRef.current = job
  }
  const [debug, setDebug] = useState<JobDebug | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!jobId) return
    const load = async () => {
      try {
        const data = await apiClient.get<Job>(`/jobs/${jobId}`)
        setJob(data)
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load job')
      }
    }
    load()
  }, [jobId])

  useEffect(() => {
    if (!jobId || !job || job.status === 'pending' || job.status === 'running') return
    const loadDebug = async () => {
      try {
        const data = await apiClient.get<JobDebug>(`/jobs/${jobId}/debug`)
        setDebug(data)
      } catch {
        // debug is optional
      }
    }
    loadDebug()
  }, [jobId, job])

  if (error) {
    return (
      <div className="p-4">
        <PageHeader title="Job Detail" />
        <div className="panel p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
          <div className="text-sm">{error}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4">
      <PageHeader
        title={`Job ${jobId?.slice(0, 8) ?? ''}`}
        subtitle="Extraction progress and diagnostics"
      >
        <button onClick={() => navigate(-1)} className="btn-secondary flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      </PageHeader>

      {jobId && (
        <JobStatusPanel
          jobId={jobId}
          fileName={job?.metadata_?.protocol_filename as string | undefined}
          onStatusChange={(updatedJob) => {
            const current = jobRef.current
            if (current && updatedJob.updated_at <= current.updated_at) {
              return
            }
            setJob(updatedJob)
          }}
        />
      )}

      {debug && (
        <div className="panel overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-500" />
              Diagnostic Summary
            </h2>
          </div>
          <div className="p-4 space-y-4 text-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-2">
                <p className="text-gray-500 dark:text-gray-400">Provider</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {debug.llm_provider} {debug.is_mock ? '(mock)' : ''}
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-2">
                <p className="text-gray-500 dark:text-gray-400">Validation</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {(debug.validation as { can_export?: boolean })?.can_export ? (
                    <span className="flex items-center gap-1 text-green-600 dark:text-green-300">
                      <CheckCircle2 className="h-4 w-4" /> Can export
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-amber-600 dark:text-amber-300">
                      <Clock className="h-4 w-4" /> Not export ready
                    </span>
                  )}
                </p>
              </div>
            </div>

            {debug.parser_warnings.length > 0 && (
              <div className="space-y-2">
                <p className="font-medium text-gray-900 dark:text-white">Warnings</p>
                <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                  {debug.parser_warnings.slice(0, 10).map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}

            {debug.raw_text_url && (
              <div>
                <a
                  href={debug.raw_text_url}
                  className="text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Download extracted raw text
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
